import { formatEuro } from '../../utils/currency';
import type { FinancialSummary } from '../../types/financialReport';

interface StatusBreakdownProps {
  summary: FinancialSummary;
  loading: boolean;
}

function ProgressBar({ segments }: { segments: { value: number; total: number; color: string; label: string }[] }) {
  const total = segments.reduce((sum, s) => sum + s.total, 0) || 1;
  return (
    <div className="space-y-2">
      <div className="h-3 rounded-full bg-gray-100 overflow-hidden flex">
        {segments.map((seg, i) => {
          const pct = (seg.value / total) * 100;
          if (pct === 0) return null;
          return (
            <div
              key={i}
              className={`${seg.color} transition-all duration-500`}
              style={{ width: `${pct}%` }}
              title={`${seg.label}: ${formatEuro(seg.value)}`}
            />
          );
        })}
      </div>
      <div className="flex flex-wrap gap-4 text-xs">
        {segments.map((seg, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <div className={`w-2.5 h-2.5 rounded-full ${seg.color}`} />
            <span className="text-gray-600">{seg.label}</span>
            <span className="font-semibold text-gray-900">{formatEuro(seg.value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function StatusBreakdown({ summary, loading }: StatusBreakdownProps) {
  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {[0, 1].map(i => (
          <div key={i} className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
            <div className="h-5 w-40 bg-gray-200 rounded animate-pulse mb-4" />
            <div className="h-3 w-full bg-gray-200 rounded animate-pulse mb-3" />
            <div className="h-4 w-60 bg-gray-200 rounded animate-pulse" />
          </div>
        ))}
      </div>
    );
  }

  const totalClient = summary.total_client_invoiced || 1;
  const totalInfluencer = summary.total_influencer_invoiced || 1;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-1">A receber de clientes</h3>
        <p className="text-xs text-gray-500 mb-4">Total faturado: {formatEuro(summary.total_client_invoiced)}</p>
        <ProgressBar
          segments={[
            { value: summary.client_paid, total: totalClient, color: 'bg-emerald-500', label: 'Pago' },
            { value: summary.client_issued, total: totalClient, color: 'bg-amber-400', label: 'Emitido' },
            { value: summary.client_overdue, total: totalClient, color: 'bg-red-500', label: 'Vencido' },
            { value: summary.client_draft + summary.client_unknown, total: totalClient, color: 'bg-gray-300', label: 'Por emitir' },
          ]}
        />
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-gray-500">Recebido</p>
              <p className="text-lg font-bold text-emerald-700">{formatEuro(summary.client_paid)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Em falta</p>
              <p className="text-lg font-bold text-amber-700">{formatEuro(summary.total_client_invoiced - summary.client_paid)}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-1">A pagar a influencers e fornecedores</h3>
        <p className="text-xs text-gray-500 mb-4">Total faturado: {formatEuro(summary.total_influencer_invoiced)}</p>
        <ProgressBar
          segments={[
            { value: summary.influencer_paid, total: totalInfluencer, color: 'bg-emerald-500', label: 'Pago' },
            { value: summary.influencer_issued, total: totalInfluencer, color: 'bg-amber-400', label: 'Por pagar' },
            { value: summary.influencer_overdue, total: totalInfluencer, color: 'bg-red-500', label: 'Vencido' },
            { value: summary.influencer_unknown, total: totalInfluencer, color: 'bg-gray-300', label: 'Por faturar' },
          ]}
        />
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-gray-500">Pago</p>
              <p className="text-lg font-bold text-emerald-700">{formatEuro(summary.influencer_paid)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Em falta</p>
              <p className="text-lg font-bold text-amber-700">{formatEuro(summary.total_influencer_invoiced - summary.influencer_paid)}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
